import { FC, type RefObject, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImageAnnotation } from '@annotorious/react';
import { getRouteApi } from '@tanstack/react-router';

import { PatchDetectionBodyLabel } from '@/api/generated';
import { SelectedDetectionSummary } from '@/components/blocks/shelf-detail/selected-detection-summary';
import { ShelfDetailSkuPrediction } from '@/components/blocks/shelf-detail/shelf-detail-sku/shelf-detail-sku-prediction';
import { SkuOverview } from '@/components/blocks/shelf-detail/sku-overview/sku-overview';
import type {
  ShelfDetailPanelSection,
  ShelfDetailPanelSections,
} from '@/components/blocks/shelf-detail/use-panel-sections';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleHeading,
} from '@/components/ui/collapsible';
import type { ImageNaturalSize } from '@/hooks/use-image-natural-size';
import {
  type DetectionLabel,
  getAnnotationProperties,
} from '@/lib/annotations';
import { cn } from '@/lib/utils';

interface SelectedAnnotationLabelPatch {
  label: DetectionLabel;
  skuMetadataId?: string | null;
}

export interface ShelfDetailPredictionsPanelProps {
  annotations: Array<ImageAnnotation>;
  imagePath: string;
  naturalSize: ImageNaturalSize | null;
  openSections: ShelfDetailPanelSections;
  onSectionOpenChange: (
    section: ShelfDetailPanelSection,
    open: boolean
  ) => void;
  /** Full selection set; drives the aggregated predictions query. */
  selectedIds: ReadonlyArray<string>;
  /** Primary selection; drives the summary card and label actions. */
  selectedId: string | null;
  labelSelectedAnnotation: (patch: SelectedAnnotationLabelPatch) => void;
  /** The scrollable sections container, for callers that scroll it. */
  listRef?: RefObject<HTMLDivElement | null>;
  className?: string;
}
const route = getRouteApi('/app/shelves/$customerId/$submissionId/$shelveId');

export const ShelfDetailPredictionsPanel: FC<
  ShelfDetailPredictionsPanelProps
> = ({
  annotations,
  imagePath,
  naturalSize,
  openSections,
  onSectionOpenChange,
  selectedIds,
  selectedId,
  labelSelectedAnnotation,
  listRef,
  className,
}: ShelfDetailPredictionsPanelProps) => {
  const { t } = useTranslation();
  const { customerId } = route.useParams();
  const selectedAnnotation = useMemo(
    () => annotations.find((annotation) => annotation.id === selectedId),
    [annotations, selectedId]
  );

  // Predictions aggregate over every committed detection in the selection.
  // Temp boxes are excluded (they have no backend detection yet, and their
  // client-generated ids would fail the API's numeric-id validation). Sorted
  // so the query key — and thus the cache entry — is independent of the order
  // the boxes were clicked in.
  const predictionDetectionIds = useMemo(() => {
    const committedIds = new Set(
      annotations
        .filter((annotation) => !getAnnotationProperties(annotation).temp)
        .map((annotation) => annotation.id)
    );

    return selectedIds.filter((id) => committedIds.has(id)).sort();
  }, [annotations, selectedIds]);

  const confirmedSkuMetadataId = selectedAnnotation?.properties?.skuMetadataId;
  const isMissingSku =
    selectedAnnotation !== undefined &&
    getAnnotationProperties(selectedAnnotation).label ===
      PatchDetectionBodyLabel.MISSING_SKU;

  const updateDetection = useCallback(
    ({
      label,
      skuMetadataId,
    }: {
      label: DetectionLabel;
      skuMetadataId?: string;
    }) => {
      if (!selectedId) return;

      labelSelectedAnnotation({ label, skuMetadataId });
    },
    [labelSelectedAnnotation, selectedId]
  );

  const setSkuForDetection = (skuMetadataId: string) => {
    updateDetection({
      label: PatchDetectionBodyLabel.ANNOTATED,
      skuMetadataId,
    });
  };

  const setLabelForDetection = (label: DetectionLabel) => {
    updateDetection({ label });
  };

  return (
    <div
      className={cn('bg-secondary flex h-full flex-col gap-2 p-2', className)}>
      {/* Stays put while the sections below scroll, so the selected
          detection is always in view. */}
      <div>
        <h3>{t('shelfDetail.selectedDetection')}</h3>
        <SelectedDetectionSummary
          className='h-56'
          annotation={selectedAnnotation}
          imagePath={imagePath}
          naturalSize={naturalSize}
          setLabel={setLabelForDetection}
        />
      </div>

      <div
        ref={listRef}
        className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto'>
        <Collapsible
          open={openSections.predictions}
          onOpenChange={(open) => {
            onSectionOpenChange('predictions', open);
          }}>
          <CollapsibleHeading
            isOpen={openSections.predictions}
            className='flex items-center'>
            <h3>
              {t('shelfDetail.predictions')}
              {predictionDetectionIds.length > 1 && (
                <span className='ml-1 text-sm font-normal text-muted-foreground'>
                  {t('shelfDetail.predictionsSelectedCount', {
                    count: predictionDetectionIds.length,
                  })}
                </span>
              )}
            </h3>
          </CollapsibleHeading>
          <CollapsibleContent>
            {predictionDetectionIds.length > 0 && (
              <ShelfDetailSkuPrediction
                className='default-fade-in'
                detectionIds={predictionDetectionIds}
                confirmedSkuMetadataId={confirmedSkuMetadataId}
                setSkuForDetection={setSkuForDetection}
                noneSelected={isMissingSku}
                onNoneOfTheAbove={() => {
                  setLabelForDetection(PatchDetectionBodyLabel.MISSING_SKU);
                }}
              />
            )}
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          open={openSections.skuList}
          onOpenChange={(open) => {
            onSectionOpenChange('skuList', open);
          }}>
          <CollapsibleHeading
            isOpen={openSections.skuList}
            className='flex items-center'>
            <h3>{t('shelfDetail.skuList')}</h3>
          </CollapsibleHeading>
          <CollapsibleContent>
            <SkuOverview
              className='default-fade-in'
              onSelectSku={setSkuForDetection}
              confirmedSkuMetadataId={confirmedSkuMetadataId}
              customerId={Number(customerId)}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};
